import { Fragment } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import PageTemplate from "../../components/ui/common/template/PageTemplate";
import ArticleList from "../../components/contents/board/ArticleList";
import CenterDiv from "../../style/styled-components/components/CenterDiv";
import ContentDiv from "../../style/styled-components/components/ContentDiv";
import UseBreakpoints from "../../components/hooks/ui/UseBreakpoints";
import { useArticles, useBriefing } from "../../components/hooks/useArticles";

// 백엔드 crawl.js 의 섹션 키 → 제목 (화면 순서)
export const SECTIONS = {
  law: "국내 법령·고시",
  hs: "최신 품목분류·판례",
  news: "관세·무역 뉴스",
  trade: "해외 통상·관세 동향",
  cbp: "통관 실무 (美 CBP)",
};

// 오늘의 핵심(홈 카드도 사용): 법령 → 뉴스 → 해외 통상 → 나머지 순으로 섹션별 최신 1건씩, 3건. items 는 날짜 내림차순
export const highlights = (items) =>
  ["law", "news", "trade", "hs", "cbp"].map((s) => items.find((it) => it.section === s)).filter(Boolean).slice(0, 3);

const isNew = (date) => Date.now() - new Date(date) < 3 * 864e5; // 게시일(UTC 자정) 기준 오늘·어제·그제

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
};

// 오늘의 브리핑: ASCP의 독립 정보 채널.
// 관세청·CLIP·KOTRA·美 연방관보 등 공식 출처 목록(백엔드가 평일 새벽 수집, /briefing) + 정운기 칼럼. 항목은 원문으로 바로 연결
const BriefingPage = () => {
  const breakpoints = UseBreakpoints();
  const items = useBriefing();
  const columns = useArticles("ceocolumn");
  const top = highlights(items);
  const latestColumns = [...columns].sort((a, b) => b.no - a.no).slice(0, 3);

  return (
    <PageTemplate title="오늘의 브리핑" noImage subHeader="관세·통상 동향을 한곳에 모아, 오늘 꼭 알아야 할 것부터 정리합니다.">
      <CenterDiv>
        <ContentDiv>
          <Head>
            <div className="date">{today()}</div>
            <div className="desc">
              관세청·KOTRA·美 연방관보 등 공식 출처의 최신 자료를 평일 새벽마다 모아 정리합니다. 제목을 누르면 원문이 새 창으로 열립니다.
            </div>
          </Head>

          <Section>오늘의 핵심</Section>
          {top.length === 0 && <Empty>브리핑을 불러오는 중입니다.</Empty>}
          <Headlines breakpoints={breakpoints}>
            {top.map((it, idx) => (
              <a key={it.url} href={it.url} target="_blank" rel="noopener noreferrer">
                <span className="no">{String(idx + 1).padStart(2, "0")}</span>
                <span className="tag">{SECTIONS[it.section]}</span>
                <span className="title">{it.title}</span>
                <span className="date"><span>{it.source}</span> · {it.date}</span>
              </a>
            ))}
          </Headlines>

          {Object.entries(SECTIONS).map(([key, title]) => {
            const list = items.filter((it) => it.section === key);
            return list.length > 0 && (
              <Fragment key={key}>
                <Section>{title}</Section>
                <Items>
                  {list.map((it) => (
                    <li key={it.url}>
                      <a href={it.url} target="_blank" rel="noopener noreferrer">
                        <span className="title">
                          {isNew(it.date) && <span className="new">NEW</span>}
                          {it.title}
                        </span>
                        <span className="meta"><span>{it.source}</span><span>{it.date}</span></span>
                      </a>
                    </li>
                  ))}
                </Items>
              </Fragment>
            );
          })}
          <More>
            <Link to="/boards/board2">관세무역 뉴스 전체 보기 →</Link>
          </More>

          <Section>에이원 인사이트 · 정운기 칼럼</Section>
          <ArticleList category="ceocolumn" contents={latestColumns} />
          <More>
            <Link to="/boards/ceocolumn">정운기 칼럼 전체 보기 →</Link>
          </More>

          <Foot>
            오늘의 브리핑은 에이원 자체 플랫폼 ASCP의 독립 정보 채널입니다. 브리핑은 일반 정보 제공 목적이며, 개별 사안은 담당 관세사와 상담하시기 바랍니다.{" "}
            <Link to="/it/ascp">ASCP 플랫폼 보기 →</Link>
          </Foot>
        </ContentDiv>
      </CenterDiv>
    </PageTemplate>
  );
};
export default BriefingPage;

const Head = styled.div`
  margin-top: 3rem;
  padding: 1.5rem 1.75rem;
  border-radius: ${({ theme }) => theme.radius};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.extralight}, ${({ theme }) => theme.colors.accentSoft});
  & > .date {
    font-size: 1.5rem;
    font-weight: 800;
    color: ${({ theme }) => theme.colors.dark};
  }
  & > .desc {
    color: ${({ theme }) => theme.colors.foreground.dimmed};
    padding-top: 0.35rem;
    line-height: 1.7;
  }
`;
const Section = styled.h3`
  font-size: 1.35rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.dark};
  margin: 3rem 0 1rem 0;
`;
const Headlines = styled.div`
  display: grid;
  grid-template-columns: ${({ breakpoints }) => (breakpoints.lg ? "repeat(3, minmax(0, 1fr))" : "minmax(0, 1fr)")};
  gap: 1rem;
  & > a {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    padding: 1.5rem;
    border-radius: ${({ theme }) => theme.radius};
    border: 1px solid ${({ theme }) => theme.colors.line};
    background-color: white;
    color: inherit;
    transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
    &:hover {
      border-color: ${({ theme }) => theme.colors.light};
      box-shadow: ${({ theme }) => theme.shadow.md};
      transform: translateY(-3px);
      & > .title {
        color: ${({ theme }) => theme.colors.accent};
      }
    }
    & > .no {
      font-size: 1.5rem;
      font-weight: 800;
      color: ${({ theme }) => theme.colors.light};
    }
    & > .tag {
      align-self: flex-start;
      font-size: 0.75rem;
      font-weight: 700;
      border-radius: 999px;
      color: ${({ theme }) => theme.colors.accent};
      background-color: ${({ theme }) => theme.colors.accentSoft};
      padding: 0.2rem 0.65rem;
    }
    & > .title {
      flex: 1;
      font-size: 1.05rem;
      font-weight: 600;
      line-height: 1.55;
      transition: color 0.15s;
    }
    & > .date {
      font-size: 0.85rem;
      color: ${({ theme }) => theme.colors.monotone.medium};
    }
  }
`;
const Items = styled.ul`
  list-style: none;
  margin: 0 0 1rem 0;
  padding: 0;
  border-top: 2px solid ${({ theme }) => theme.colors.dark};
  & > li > a {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.3rem 1rem;
    padding: 1rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.line};
    color: inherit;
    transition: background-color 0.15s;
    &:hover {
      background-color: ${({ theme }) => theme.colors.monotone.extralight};
      & > .title {
        color: ${({ theme }) => theme.colors.accent};
      }
    }
    @media (max-width: 575px) {
      padding: 1rem 0.25rem;
    }
  }
  & .new {
    margin-right: 0.5rem;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.03em;
    vertical-align: 0.1em;
    color: ${({ theme }) => theme.colors.accent};
  }
  & .title {
    flex: 1 1 24rem;
    min-width: 0;
    font-size: 1.05rem;
    font-weight: 500;
    line-height: 1.55;
    overflow-wrap: anywhere;
    color: ${({ theme }) => theme.colors.foreground.normal};
    transition: color 0.15s;
  }
  & .meta {
    display: flex;
    gap: 0.75rem;
    font-size: 0.85rem;
    color: ${({ theme }) => theme.colors.monotone.medium};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
`;
const More = styled.div`
  display: flex;
  justify-content: flex-end;
  & > a {
    font-weight: 700;
    color: ${({ theme }) => theme.colors.accent};
    &:hover {
      text-decoration: underline;
    }
  }
`;
const Empty = styled.div`
  padding: 2rem 0;
  color: ${({ theme }) => theme.colors.foreground.dimmed};
`;
const Foot = styled.div`
  margin-top: 3.5rem;
  padding: 1.25rem 1.5rem;
  border-radius: ${({ theme }) => theme.radius};
  background-color: ${({ theme }) => theme.colors.monotone.extralight};
  font-size: 0.9rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.foreground.dimmed};
  & a {
    color: ${({ theme }) => theme.colors.accent};
    font-weight: 700;
  }
`;
